import React from 'react';

import { useIntl } from 'react-intl';

import PieChart from 'src/features/statistic/components/chart/PieChart';
import MapLegend from 'src/features/statistic/components/MapLegend';
import { TOTAL_VISITS_KEY, VisitStatisticTotals } from 'src/features/statistic/types';
import { colorCategoryMapping } from 'src/features/statistic/utils/constants';
import { statisticMessages } from 'src/features/statistic/utils/messages';
import { GeneralBlock } from 'src/registry/components/blocks/GeneralBlock';
import GeneralMessageBlock from 'src/registry/components/blocks/GeneralMessageBlock';
import { SectionBlockTitle } from 'src/registry/components/blocks/SectionBlock';
import { Skeleton } from 'src/registry/ui/Skeleton';
import { ICON_MAP } from 'src/utils/constants';

type Props = {
    isLoading: boolean;
    data?: VisitStatisticTotals;
};

type FormattedData = {
    title: string;
    value: number;
}[];

export default function WidgetTotalVisits({ isLoading, data }: Props) {
    const { formatMessage } = useIntl();

    const formattedData: FormattedData = React.useMemo(() => {
        if (!data) return [];
        return Object.entries(data)
            .filter(([key, value]) => value > 0 && key !== TOTAL_VISITS_KEY)
            .map(([key, value]) => ({
                title: key,
                value,
            }));
    }, [data]);

    const legendColorMapping: Record<string, string> = React.useMemo(() => {
        // A category stays in the legend only while the chart above actually draws a slice for it.
        // "formattedData" is already filtered to the values above zero, which keeps the legend from
        // ever listing a category the donut says nothing about
        const categoriesWithVisits: Set<string> = new Set(formattedData.map(({ title }) => title));

        return Object.fromEntries(
            Object.entries(colorCategoryMapping).filter(([category]) => categoriesWithVisits.has(category)),
        );
    }, [formattedData]);

    return (
        <GeneralBlock className="min-h-110 flex-1">
            <SectionBlockTitle>{formatMessage(statisticMessages.totalVisits)}</SectionBlockTitle>
            {isLoading ? (
                <Skeleton className="h-90" />
            ) : data ? (
                <div className="flex h-full w-full flex-col justify-center gap-2 px-10">
                    <PieChart
                        data={formattedData}
                        dataKey="value"
                        isLoading={isLoading}
                        title={formatMessage(statisticMessages.totalVisits)}
                        value={data.total_visits_count}
                    />

                    <MapLegend colorMapping={legendColorMapping} />
                </div>
            ) : (
                <GeneralMessageBlock
                    icon={ICON_MAP.SEARCH}
                    title={formatMessage(statisticMessages.noPaymentsFound)}
                    subtitle={formatMessage(statisticMessages.chooseDifferentPeriod)}
                />
            )}
        </GeneralBlock>
    );
}
